import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Row, Col, Button, Select, Form, DatePicker, Input, Modal, Typography, Tooltip, notification } from 'antd';
import {
  intialPaginationState,
  formItemLayout,
  tailFormItemLayout,
  onlyNumbers,
  onlyNumber,
  validatePair,
  unexpectedError,
} from '../../../common_functions';
import AdvanceNegativeSearch from '../../negativeFilter/AdvanceModal';
import * as userActions from '../../../redux/actions/userActions';
import { useDispatch } from 'react-redux';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
const { RangePicker } = DatePicker;

const ImageAdvanceFilter = ({ visible, onClose, props, resetDataState, resetData, fetchReports: getDataReports }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [conceptPortfolios, setConceptPortfolios] = useState([]);
  const [filteredStampData, setFilteredStampData] = useState([]);
  const [childStamps, setChildStamps] = useState([]);
  const [handWritingNotes, setHandWritingNotes] = useState([]);
  const [negatives, setNegatives] = useState([]);
  const [signatures, SetSignatures] = useState([]);
  const [negFormVisible, setNegFormVisible] = useState(false);
  const [selectedNegative, setSelectedNegative] = useState(null);

  //Get collection data
  useEffect(() => {
    const getDefaultData = async () => {
      try {
        setLoading(true);
        // await getArtists();
        await getSignatures();
        const sData = await getStamps();
        setFilteredStampData(sData);
        await getHandWritingNotes();
        await getConcetualPortfolio();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getDefaultData();
  }, []);

  useEffect(() => {
    formRef?.current?.setFieldsValue({ negative: selectedNegative });
  }, [selectedNegative]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!resetData) return;
      setLoading(true);
      await getDataReports(intialPaginationState, { object_type: { _eq: 'print' } });
      if (isMounted) {
        resetDataState(false);
        formRef.current?.resetFields();
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [resetData]);

  const getSignatures = async () => {
    const { data: signData } = await dispatch(
      userActions.getItems('signatures', {
        fields: ['id', 'signature'],
        limit: -1,
      })
    );
    SetSignatures(signData);
  };

  const getHandWritingNotes = async () => {
    const { data } = await dispatch(
      userActions.getItems('hand_writing_notes', {
        fields: ['id', 'name'],
        sort: 'name',
        limit: -1,
      })
    );
    setHandWritingNotes(data);
  };
  const getStamps = async (isSubgroup = false, parentStamp) => {
    const { data } = await dispatch(
      userActions.getItems('stamps', {
        fields: ['id', 'stamp_name', 'height', 'width'],
        sort: 'stamp_name',
        limit: -1,
        filters: parentStamp
          ? { parent_stamp: { _eq: parentStamp }, is_subgroup: { _eq: isSubgroup } }
          : { is_subgroup: { _eq: isSubgroup } },
      })
    );
    return data;
  };
  const showSubgroup = async parentStamp => {
    let data = await getStamps(true, parentStamp);
    if (data?.length > 0) {
      formRef.current.setFieldsValue({ stamp_subgroup: null });
    }
    setChildStamps(data);
  };

  const getConcetualPortfolio = async () => {
    const { data: portfolio } = await dispatch(
      userActions.getItems('portfolio', {
        fields: ['id', 'name'],
        limit: '-1',
        sort: 'name',
        filters: { type: { _eq: process.env.conceptual_work_id } },
      })
    );
    setConceptPortfolios(portfolio);
  };

  const setFilters = values => {
    let filters = { object_type: { _eq: 'print' } };
    let conditions = [];
    const addCondition = condition => condition && conditions.push(condition);
    // Helper function to format date
    const formatDate = date => dayjs(date).startOf('year').toISOString();
    // Date Range Filters
    if (values?.print_date_range) {
      const [start, end] = values.print_date_range;
      addCondition({ print: { creation_date_start_year: { _gte: formatDate(start) } } });
      addCondition({ print: { creation_date_end_year: { _lte: formatDate(end) } } });
    }

    if (values?.negative_date_range) {
      const [start, end] = values.negative_date_range;
      addCondition({ print: { negative_id: { creation_earliest: { _gte: formatDate(start) } } } });
      addCondition({ print: { negative_id: { creation_latest: { _lte: formatDate(end) } } } });
    }
    if (values?.image_dimension_height) {
      const height = Number(values.image_dimension_height);
      const range = Number(values.image_dimension_height_range) || 0;
      const minHeight = Math.max(0, height - range);
      const maxHeight = height + range;

      addCondition({ print: { image_dimension_height: { _gte: minHeight } } });
      addCondition({ print: { image_dimension_height: { _lte: maxHeight } } });
    }
    if (values?.image_dimension_width) {
      const width = Number(values.image_dimension_width);
      const range = Number(values.image_dimension_width_range) || 0;
      const minWidth = Math.max(0, width - range);
      const maxWidth = width + range;
      addCondition({ print: { image_dimension_width: { _gte: minWidth } } });
      addCondition({ print: { image_dimension_width: { _lte: maxWidth } } });
    }
    if (values?.signature_verso) {
      addCondition({ print: { basic_photos: { signature_verso_text: { _eq: values.signature_verso } } } });
    }
    if (values?.signature_recto) {
      addCondition({ print: { basic_photos: { signature_recto_text: { _eq: values.signature_recto } } } });
    }

    // General Filters
    const mappings = [
      { key: 'print', field: 'portfolio_add_number', value: values?.portfolio },
      { key: 'print', field: 'stamp', value: values?.stamp_subgroup ?? values?.stamp },
      { key: 'paper_group', field: 'id', value: values?.paper_group },
      { key: 'print', field: 'negative_id', subField: 'id', value: values?.negative },
    ];

    mappings.forEach(({ key, field, subField, value }) => {
      if (value) {
        addCondition(subField ? { [key]: { [field]: { [subField]: { _eq: value } } } } : { [key]: { [field]: { _eq: value } } });
      }
    });

    // Annotation Filters
    ['annotation_recto_handwriting', 'annotation_verso_handwriting', 'stamp_verso'].forEach(key => {
      if (values?.[key]) {
        addCondition({
          print: {
            basic_photos: {
              [key.includes('verso') ? (key.includes('stamp') ? 'stamp_verso' : 'annotations_verso') : 'annotations_recto']: {
                hand_writing_notes: { _eq: values[key] },
              },
            },
          },
        });
      }
    });

    return { _and: [filters, ...conditions] };
  };

  const resetFilters = async () => {
    formRef.current?.resetFields();
    try {
      await getDataReports(intialPaginationState, { object_type: { _eq: 'print' } });
      notification.success({ message: 'Search filters are reset Successfully!', duration: 2 });
    } catch (error) {
      console.log(error);
      unexpectedError(error);
    }
  };
  const onFinish = async values => {
    try {
      setLoading(true);
      await getDataReports(intialPaginationState, setFilters(values));
      notification.success({ message: 'Results updated based on your search.', duration: 2 });
      onClose();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const closeNegativeModal = () => {
    setNegFormVisible(false);
  };

  return (
    <>
      <Modal title="Advanced Image Search Filters" open={visible} onCancel={onClose} footer={false} width={1200}>
        <Row className={!!loading ? 'disabledComponent' : ''}>
          <Col span={24}>
            <Row gutter={24} align="middle" style={{ marginBottom: 10 }}>
              <Col xs={12}>
                <Typography.Title level={4}>Image Search Filters</Typography.Title>
              </Col>
            </Row>
            <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item
                    name="negative"
                    className="labelLink"
                    label={
                      <div
                        style={{
                          justifyContent: 'space-between',
                          width: '100%',
                          textAlign: 'center',
                          display: 'flex',
                        }}
                      >
                        Negative
                        <Tooltip placement="rightTop" title="Advance Data Reporting">
                          <a style={{ color: '#a6a300', cusrsor: 'pointer' }} onClick={() => setNegFormVisible(true)}>
                            Advance filter
                          </a>
                        </Tooltip>
                      </div>
                    }
                  >
                    <Select
                      placeholder="Select Image/Negative"
                      optionFilterProp="children"
                      showSearch
                      allowClear
                      filterOption={(input, option) => option.children.includes(input)}
                      onChange={e => {
                        setSelectedNegative(e);
                      }}
                    >
                      {negatives?.map(({ negative: { id, obj_name } }) => (
                        <Select.Option key={id} value={id}>
                          {obj_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="negative_date_range" label="Negative Date Range">
                    <RangePicker picker="year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="print_date_range" label="Print Date Range">
                    <RangePicker picker="year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item
                    name="image_dimension_height"
                    label="Image Dimension Height (mm)"
                    rules={[
                      { validator: onlyNumber },
                      validatePair(
                        'image_dimension_height_range',
                        'Both fields, "Image Dimension Height (mm)" and "+/- Image Dimension Height (mm)" are required if either one is filled!'
                      ),
                    ]}
                  >
                    <Input onKeyPress={onlyNumbers} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item
                    name="image_dimension_height_range"
                    label="+/- Image Dimension Height (mm)"
                    rules={[
                      { validator: onlyNumber },
                      validatePair(
                        'image_dimension_height',
                        'Both fields, "Image Dimension Height (mm)" and "+/- Image Dimension Height (mm)" are required if either one is filled!'
                      ),
                    ]}
                  >
                    <Input onKeyPress={onlyNumbers} placeholder="+/- height" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item
                    name="image_dimension_width"
                    label="Image Dimension Width (mm)"
                    rules={[
                      { validator: onlyNumber },
                      validatePair(
                        'image_dimension_width_range',
                        'Both fields, "Image Dimension Width (mm)" and "+/- Image Dimension Width (mm)" are required if either one is filled!'
                      ),
                    ]}
                  >
                    <Input onKeyPress={onlyNumbers} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item
                    name="image_dimension_width_range"
                    label="+/- Image Dimension Width (mm)"
                    rules={[
                      { validator: onlyNumber },
                      validatePair(
                        'image_dimension_width',
                        'Both fields, "Image Dimension Width  (mm)" and "+/- Image Dimension Width (mm)" are required if either one is filled!'
                      ),
                    ]}
                  >
                    <Input onKeyPress={onlyNumbers} placeholder="+/- width" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="portfolio" label="Portfolio">
                    <Select placeholder="Select Portfolio" optionFilterProp="children" showSearch allowClear>
                      {conceptPortfolios.map(col => {
                        return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="signature_recto" label="Signature Recto">
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      placeholder="Select signature"
                      style={{ textTransform: 'capitalize' }}
                    >
                      {signatures?.map(el => (
                        <Select.Option key={el.id} value={el.id}>
                          {el.signature}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="signature_verso" label="Signature Verso">
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      placeholder="Select signature"
                      style={{ textTransform: 'capitalize' }}
                    >
                      {signatures?.map(el => (
                        <Select.Option key={el.id} value={el.id}>
                          {el.signature}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="stamp" label="Stamp">
                    <Select
                      showSearch
                      allowClear
                      virtual={false}
                      filterOption={false}
                      placeholder="Select stamp"
                      style={{ textTransform: 'capitalize' }}
                      onChange={showSubgroup}
                    >
                      {filteredStampData?.map(el => (
                        <Select.Option key={el.id} value={el.id}>
                          {el.stamp_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {childStamps?.length > 0 && (
                  <Col xs={24} sm={12} md={8} lg={8}>
                    <Form.Item name="stamp_subgroup" label="Stamp Subgroup">
                      <Select
                        showSearch
                        allowClear
                        virtual={false}
                        filterOption={false}
                        placeholder="Select stamp"
                        style={{ textTransform: 'capitalize' }}
                      >
                        {childStamps?.map(el => (
                          <Select.Option key={el.id} value={el.id} onMouseEnter={() => {}} onMouseLeave={() => {}}>
                            {el.stamp_name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="stamp_verso" label="Stamp Handwriting">
                    <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                      {handWritingNotes.map(col => {
                        return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="annotation_recto_handwriting" label="Annotation Recto Handwriting">
                    <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                      {handWritingNotes.map(col => {
                        return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="annotation_verso_handwriting" label="Annoation Verso Handwriting">
                    <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                      {handWritingNotes.map(col => {
                        return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item style={{ float: 'right' }}>
                    <Button
                      type="primary"
                      size="small"
                      htmlType="submit"
                      style={{
                        marginTop: '28px',
                        height: '42px',
                      }}
                      icon={<SearchOutlined />}
                    >
                      Search
                    </Button>
                    <Button
                      style={{
                        marginLeft: '10px',
                        height: '42px',
                        marginTop: '28px',
                      }}
                      size="small"
                      danger
                      onClick={resetFilters}
                      icon={<ReloadOutlined />}
                    >
                      Reset Filters
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>

      <AdvanceNegativeSearch
        visible={negFormVisible}
        onClose={closeNegativeModal}
        props={props}
        selectedNegative={selectedNegative}
        setSelectedNegative={setSelectedNegative}
        setNegatives={setNegatives}
        reportType="Image"
      />
    </>
  );
};
export default ImageAdvanceFilter;
